/**
 * Run a task once the page has settled: after `load`, then an idle callback.
 * Shared by the work that must stay off the boot path but still start on its
 * own — Sentry Replay (`lib/lazySentryReplay.ts`) and the Mixpanel session
 * recorder (`lib/mixpanelClient.ts`).
 *
 * The task runs exactly once, whichever comes first:
 * - `load` (or now, if the document already loaded), then `requestIdleCallback`
 *   with `idleTimeoutMs` as its timeout (`setTimeout(…, 0)` where
 *   `requestIdleCallback` is missing, i.e. Safari);
 * - `ceilingMs` after install, so a boot stuck before `load` still gets the
 *   task long before the ~45 s `boot-splash-stuck` report (CUSTOMER-UI-3Z
 *   triage needs a replay). Deliberately NOT gated on app-ready: a stuck
 *   splash never gets there.
 *
 * Import-free and window-injected so it is unit-testable without fake global
 * timers (lib/ specs share one process).
 */

/** `requestIdleCallback` timeout once `load` has fired. */
export const AFTER_LOAD_IDLE_TIMEOUT_MS = 2_000;

/** Run no later than this after install, even if `load` never fires. */
export const AFTER_LOAD_CEILING_MS = 10_000;

/** The slice of `window` the scheduler touches, injectable for tests. */
export interface AfterLoadIdleWindow {
  document: { readyState: DocumentReadyState };
  addEventListener(type: "load", listener: () => void): void;
  removeEventListener(type: "load", listener: () => void): void;
  setTimeout(handler: () => void, timeout: number): number;
  clearTimeout(id: number): void;
  requestIdleCallback?: (
    callback: () => void,
    options: { timeout: number }
  ) => number;
  cancelIdleCallback?: (id: number) => void;
}

/**
 * Schedules `task`; returns a disposer that cancels it if it has not run yet
 * and removes every listener and timer either way.
 */
export function runAfterLoadIdle(
  task: () => void,
  {
    win,
    idleTimeoutMs = AFTER_LOAD_IDLE_TIMEOUT_MS,
    ceilingMs = AFTER_LOAD_CEILING_MS,
  }: { win: AfterLoadIdleWindow; idleTimeoutMs?: number; ceilingMs?: number }
): () => void {
  let done = false;
  let ceilingTimer: number | undefined;
  let idleTimer: number | undefined;
  let idleHandle: number | undefined;

  const clearTriggers = () => {
    win.removeEventListener("load", onLoad);
    if (ceilingTimer !== undefined) win.clearTimeout(ceilingTimer);
    if (idleTimer !== undefined) win.clearTimeout(idleTimer);
    if (idleHandle !== undefined) win.cancelIdleCallback?.(idleHandle);
    ceilingTimer = idleTimer = idleHandle = undefined;
  };

  const run = () => {
    if (done) return;
    done = true;
    clearTriggers();
    task();
  };

  function onLoad() {
    win.removeEventListener("load", onLoad);
    if (done) return;
    if (typeof win.requestIdleCallback === "function") {
      idleHandle = win.requestIdleCallback(run, { timeout: idleTimeoutMs });
    } else {
      idleTimer = win.setTimeout(run, 0);
    }
  }

  ceilingTimer = win.setTimeout(run, ceilingMs);
  if (win.document.readyState === "complete") onLoad();
  else win.addEventListener("load", onLoad);

  return () => {
    done = true;
    clearTriggers();
  };
}
